import { useAgencyApi, useCurrentAgency, usePageNotifications } from '.'
import { useInvokeApi } from 'hooks'
import { considerDataToBeStale } from 'utils'
import { MapLayer } from 'api'
import { AxiosError } from 'axios'

export const useCurrentAgencyCustomMapLayers = (
  licensedAgencyId: string | undefined | null,
) => {
  const agencyApi = useAgencyApi()
  const invokeApi = useInvokeApi()
  const {
    state: currentAgencyContext,
    dispatch: dispatchAgencyAction,
  } = useCurrentAgency(licensedAgencyId)

  const loadCustomMapLayers = async (force?: boolean): Promise<void> => {
    const isStale = considerDataToBeStale(
      currentAgencyContext.customMapLayers.data,
      currentAgencyContext.customMapLayers.lastApiReceiveAt,
    )
    if (!force && !isStale) {
      return Promise.resolve()
    }

    await invokeApi(
      () => agencyApi.getCustomMapLayers(),
      'An error occurred while loading custom map layers.',
      (result) => {
        dispatchAgencyAction({
          type: 'LoadCustomMapLayers',
          payload: result.mapLayers,
          timestamp: Date.now(),
        })
      },
    )
  }

  const getFileError = (error: any) => {
    const axiosError = error as AxiosError;
    if (axiosError.isAxiosError && !!axiosError.response)
    {
      const errors = error.response.data as { [key: string]: string[] }
      var fileErrors = errors['File']
      if (fileErrors) {
        return fileErrors.join("\n")
      }
    }

    return undefined
  }

  const addCustomMapLayer = async (
    customMapLayer: MapLayer,
    file: File,
    onProgress?: (event: ProgressEvent) => void
  ): Promise<{ success: boolean, fileError?: string }> => {
    try {
      const response = await agencyApi.addCustomMapLayer(customMapLayer, file, onProgress)
      dispatchAgencyAction({
        type: 'AddCustomMapLayer',
        payload: response.data.data,
      })

      return { success: true }
    }
    catch (ex)
    {
      return { success: false, fileError: getFileError(ex) }
    }
  }

  const editCustomMapLayer = async (
    customMapLayer: MapLayer,
    file?: File,
    onProgress?: (event: ProgressEvent) => void
  ): Promise<{ success: boolean, fileError?: string }> => {
    try {
      const response = await agencyApi.editCustomMapLayer(customMapLayer, file, onProgress)
      dispatchAgencyAction({
        type: 'EditCustomMapLayer',
        payload: response.data.data,
      })

      return { success: true }
    }
    catch (ex)
    {
      return { success: false, fileError: getFileError(ex) }
    }
  }

  const deleteCustomMapLayer = async (
      customMapLayerId: string,
      onSuccess: () => void,
    ): Promise<void> => {
    await invokeApi(
      () => agencyApi.deleteCustomMapLayer(customMapLayerId),
      'An error occurred while deleting the custom map layer.',
      undefined,
      () => {
        dispatchAgencyAction({
          type: 'DeleteCustomMapLayer',
          payload: customMapLayerId,
        })

        onSuccess()
      },
    )
  }

  return {
    customMapLayers: currentAgencyContext.customMapLayers.data ?? [],
    customMapLayerLookup: currentAgencyContext.customMapLayerLookup,
    loadCustomMapLayers: loadCustomMapLayers,
    addCustomMapLayer: addCustomMapLayer,
    editCustomMapLayer: editCustomMapLayer,
    deleteCustomMapLayer: deleteCustomMapLayer,
  }
}
